import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  ArrowLeft, RefreshCw, ChevronRight, X, ArrowRight, Search,
  Send, XCircle, ServerCrash, Clock, ListChecks, CheckCircle2, Repeat, Ban, Phone, Hash, FileWarning, Flag, Zap, AlertTriangle,
} from 'lucide-react';
import { EngagementTabs, StatCard, DotBadge, CopyableId, CampaignStatusHelp } from '../components/engagement/primitives';
import {
  fetchEngagementSent,
  fetchMessageLogs,
} from '../services/engagementService';

// Status values come from EngagementSent.last_attempt_status
// (events/services/reminder_service.py / events/tasks.py), NOT
// cep/documents/engagement_message_log.py's status enum — this page shows
// events/'s reminder-send ledger, since cep/'s own EngagementMessageLog
// stays empty until cep/'s separate dispatch pathway (cep.events.process_event)
// actually runs.
// Every value events.tasks.py's _dispatch_reminder actually sets on
// EngagementSent.last_attempt_status (shared by both the event-triggered
// and the recurring send paths — same statuses either way), plus the
// model's own default ('pending', never explicitly set by _dispatch_reminder
// but present on rows nothing has attempted yet).
const STATUS_OPTIONS = [
  { value: 'sent', label: 'Sent', colorClass: 'bg-emerald-50 text-emerald-700 ring-emerald-600/20', dotClass: 'bg-emerald-500' },
  { value: 'client_error', label: 'Failed (4xx)', colorClass: 'bg-red-50 text-red-700 ring-red-600/20', dotClass: 'bg-red-500' },
  { value: 'server_error', label: 'Failed (5xx)', colorClass: 'bg-red-50 text-red-700 ring-red-600/20', dotClass: 'bg-red-500' },
  { value: 'skipped_no_template', label: 'Skipped (no template)', colorClass: 'bg-amber-50 text-amber-700 ring-amber-600/20', dotClass: 'bg-amber-500' },
  { value: 'pending', label: 'Pending', colorClass: 'bg-slate-100 text-slate-600 ring-slate-500/20', dotClass: 'bg-slate-400' },
];

const STATUS_LABEL = STATUS_OPTIONS.reduce((m, s) => { m[s.value] = s; return m; }, {});

const RECURRING_STATUS_LABEL = {
  scheduled: { label: 'Scheduled', colorClass: 'bg-blue-50 text-blue-700 ring-blue-600/20', dotClass: 'bg-blue-500' },
  completed_cap: { label: 'Cap reached', colorClass: 'bg-slate-100 text-slate-600 ring-slate-500/20', dotClass: 'bg-slate-400' },
  stopped_converted: { label: 'Converted', colorClass: 'bg-emerald-50 text-emerald-700 ring-emerald-600/20', dotClass: 'bg-emerald-500' },
  stopped_rule_changed: { label: 'Rule changed', colorClass: 'bg-amber-50 text-amber-700 ring-amber-600/20', dotClass: 'bg-amber-500' },
  stopped_error: { label: 'Stopped (error)', colorClass: 'bg-red-50 text-red-700 ring-red-600/20', dotClass: 'bg-red-500' },
};

/* Campaign filter temporarily disabled — remove this comment wrapper to restore.
// Same six values CampaignStatusHelp documents — 'not_recurring' is a
// frontend-only pseudo-value the backend maps back to recurring_status=None
// (see events/api/views_engagement_sent/list.py), since None isn't itself
// a RECURRING_STATUSES choice.
const RECURRING_STATUS_OPTIONS = [
  ...Object.entries(RECURRING_STATUS_LABEL).map(([value, meta]) => ({ value, label: meta.label })),
  { value: 'not_recurring', label: 'Not recurring' },
];
*/

const KPI_CARDS = [
  { key: 'sent', label: 'Sent', icon: Send, accent: { bg: 'bg-emerald-50', fg: 'text-emerald-600', bar: 'bg-emerald-500' } },
  { key: 'client_error', label: 'Failed (4xx)', icon: XCircle, accent: { bg: 'bg-red-50', fg: 'text-red-600', bar: 'bg-red-500' } },
  { key: 'server_error', label: 'Failed (5xx)', icon: ServerCrash, accent: { bg: 'bg-orange-50', fg: 'text-orange-600', bar: 'bg-orange-500' } },
  { key: 'skipped_no_template', label: 'Skipped', icon: FileWarning, accent: { bg: 'bg-amber-50', fg: 'text-amber-600', bar: 'bg-amber-500' } },
  { key: 'pending', label: 'Pending', icon: Clock, accent: { bg: 'bg-slate-50', fg: 'text-slate-600', bar: 'bg-slate-400' } },
];

const PAGE_SIZE = 25;

// Force IST regardless of the admin's own machine/browser timezone —
// toLocaleString() with no options renders in the VIEWER's local tz, which
// silently shows UTC (or anything else) for anyone not already set to IST,
// even though the backend timestamps are correctly UTC-stamped instants.
const fmt = (iso) => {
  if (!iso) return '—';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '—';
  return `${d.toLocaleString('en-IN', { timeZone: 'Asia/Kolkata', dateStyle: 'short', timeStyle: 'medium' })} IST`;
};

const StatusBadge = ({ status }) => {
  const meta = STATUS_LABEL[status] || { label: status || '—', colorClass: 'bg-slate-100 text-slate-600 ring-slate-500/20', dotClass: 'bg-slate-400' };
  return <DotBadge label={meta.label} colorClass={meta.colorClass} dotClass={meta.dotClass} />;
};

const RecurringStatusBadge = ({ status }) => {
  if (!status) return <DotBadge label="Not recurring" colorClass="bg-gray-50 text-gray-400 ring-gray-300" dotClass="bg-gray-300" />;
  const meta = RECURRING_STATUS_LABEL[status] || { label: status, colorClass: 'bg-slate-100 text-slate-600 ring-slate-500/20', dotClass: 'bg-slate-400' };
  return <DotBadge label={meta.label} colorClass={meta.colorClass} dotClass={meta.dotClass} />;
};

// Every field EngagementSent captures (see cep/documents/engagement_sent.py
// and events/api/views_engagement_sent/list.py's _serialize), laid out as a
// flat table rather than a raw JSON dump.
// Short relative-time label ("3h ago", "in 2d") for timeline nodes — the
// modal already shows the absolute timestamp alongside it, this is just a
// faster-to-scan hint.
const relativeTime = (iso) => {
  if (!iso) return null;
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return null;
  const diffMs = d.getTime() - Date.now();
  const abs = Math.abs(diffMs);
  const mins = Math.round(abs / 60000);
  const label = mins < 60 ? `${mins}m` : mins < 1440 ? `${Math.round(mins / 60)}h` : `${Math.round(mins / 1440)}d`;
  return diffMs < 0 ? `${label} ago` : `in ${label}`;
};

const InfoChip = ({ icon: Icon, children }) => (
  <span className="inline-flex items-center gap-1.5 text-sm text-gray-600">
    <Icon className="w-3.5 h-3.5 text-gray-400 shrink-0" /> {children}
  </span>
);

// A single node in the horizontal send-attempt timeline — a colored dot,
// a label, and the timestamp beneath it. `state` drives the dot's color so
// the whole strip reads left-to-right as "what happened, in order" without
// needing to parse any text.
const TimelineNode = ({ icon: Icon, label, timestamp, state, isLast }) => {
  // Each state gets its own color so the strip reads left-to-right without
  // needing to read the labels — 'done' (Converted) is the only "success"
  // green; a mid-sequence send is neutral blue, a 5xx/4xx failure and a
  // template-skip are visually distinct from each other, not all lumped
  // into one generic "problem" red.
  const STATE_STYLE = {
    sent: 'bg-blue-500 text-white',
    reminder: 'bg-violet-500 text-white',
    delivered: 'bg-teal-500 text-white',
    done: 'bg-emerald-500 text-white',
    active: 'bg-blue-500 text-white',
    failed: 'bg-red-500 text-white',
    failed5xx: 'bg-orange-500 text-white',
    skipped: 'bg-amber-500 text-white',
    capped: 'bg-slate-400 text-white',
    pending: 'bg-gray-200 text-gray-400',
    overdue: 'bg-amber-500 text-white',
  };
  const CONNECTOR_STYLE = {
    sent: 'bg-blue-200',
    reminder: 'bg-violet-200',
    delivered: 'bg-teal-200',
    done: 'bg-emerald-300',
    active: 'bg-blue-200',
    failed: 'bg-red-200',
    failed5xx: 'bg-orange-200',
    skipped: 'bg-amber-200',
    capped: 'bg-slate-200',
    pending: 'bg-gray-200',
    upcoming: 'bg-gray-200',
    overdue: 'bg-amber-200',
  };
  // 'upcoming' hasn't happened yet — a dashed outline instead of a solid
  // fill so it visually reads as "not yet", distinct from every completed
  // (solid-filled) node.
  const isUpcoming = state === 'upcoming';
  return (
    <div className="flex items-start shrink-0">
      <div className="flex flex-col items-center gap-1.5 shrink-0 w-20">
        <div className={
          isUpcoming
            ? 'w-9 h-9 rounded-full flex items-center justify-center shrink-0 bg-white border-2 border-dashed border-gray-300 text-gray-400'
            : `w-9 h-9 rounded-full flex items-center justify-center shrink-0 ${STATE_STYLE[state] || STATE_STYLE.pending}`
        }>
          <Icon className="w-4 h-4" />
        </div>
        <p className={`text-xs font-medium text-center leading-tight ${isUpcoming ? 'text-gray-400 italic' : 'text-gray-700'}`}>{label}</p>
        {timestamp && (
          <div className="text-center">
            <p className="text-[11px] text-gray-600 font-medium whitespace-nowrap">{relativeTime(timestamp)}</p>
            <p className="text-[10px] text-gray-500 leading-tight">{fmt(timestamp)}</p>
          </div>
        )}
      </div>
      {/* Connector must line up with the icon circle's vertical center
          (h-9 = 2.25rem, so 1.125rem down), not the whole column's center —
          items-center on the row would center it against the label/timestamp
          text below the circle too, pulling the line off the icons. Fixed
          width (not flex-1) since the timeline row now scrolls horizontally
          instead of squeezing nodes to fit — flex-1 here would stretch to
          fill leftover scroll-container width instead of staying a short tick. */}
      {!isLast && <div className={`h-0.5 w-6 mt-[1.0625rem] shrink-0 ${CONNECTOR_STYLE[state] || CONNECTOR_STYLE.pending}`} />}
    </div>
  );
};

// One EngagementMessageLog row per dispatch attempt (see
// cep/documents/engagement_message_log.py) — the real per-attempt audit
// trail, each with its own attempted_at. This is what makes an actual
// per-reminder timeline possible; EngagementSent itself only carries a
// running reminders_sent COUNT plus the latest attempt's timestamp.
const ATTEMPT_NODE_STYLE = {
  sent: { icon: Send, label: 'Sent', state: 'sent' },
  failed_4xx: { icon: XCircle, label: 'Failed (4xx)', state: 'failed' },
  failed_5xx: { icon: ServerCrash, label: 'Failed (5xx)', state: 'failed5xx' },
  failed_unknown: { icon: XCircle, label: 'Failed', state: 'failed' },
  skipped_no_template: { icon: FileWarning, label: 'Skipped', state: 'skipped' },
  skipped_idempotent: { icon: Ban, label: 'Skipped', state: 'skipped' },
  skipped_check_failed: { icon: Ban, label: 'Skipped', state: 'skipped' },
  skipped_user_unreachable: { icon: Ban, label: 'Skipped', state: 'skipped' },
};

const LogDetailModal = ({ log, onClose }) => {
  const [attempts, setAttempts] = useState(null);
  const [attemptsError, setAttemptsError] = useState(false);

  useEffect(() => {
    if (!log) return;
    let cancelled = false;
    setAttempts(null);
    setAttemptsError(false);
    fetchMessageLogs({ engagement_sent_id: log.id, page_size: 100 }).then((res) => {
      if (cancelled) return;
      if (!res.metadata.api_status) {
        setAttemptsError(true);
        return;
      }
      setAttempts([...res.msg.results].sort(
        (a, b) => new Date(a.attempted_at) - new Date(b.attempted_at),
      ));
    });
    return () => { cancelled = true; };
  }, [log]);

  if (!log) return null;
  const userName = log.user?.full_name || [log.user?.first_name, log.user?.last_name].filter(Boolean).join(' ');
  const isFailed = log.last_attempt_status === 'client_error' || log.last_attempt_status === 'server_error';
  const isRecurring = Boolean(log.recurring_status);
  // Prefer the rule's actual configured cap (max_reminders) — falls back to
  // the old best-effort guess only for rows fetched before this field
  // existed. Without the real cap, "how many total sends can this campaign
  // ever have" was being guessed as reminders_sent+1, which had no ceiling
  // and could keep growing a "Reminder N" label past the real max.
  const maxKnown = log.max_reminders || (
    log.recurring_status === 'completed_cap' ? (log.reminders_sent || 1) : Math.max(log.reminders_sent || 0, 1) + (log.next_reminder_at ? 1 : 0)
  );

  // Real send timeline, one node per send slot. Each node uses its actual
  // EngagementMessageLog attempt record (real status + attempted_at) when
  // one exists at that position; reminders_sent (EngagementSent's own
  // counter) is the floor on how many slots existed even for rows that
  // predate the audit-log wiring (e.g. seeded/legacy rows) — never let the
  // node count fall below it, pad the gap with untimestamped placeholders
  // instead of dropping those reminders from the picture. Slot 0 is always
  // "1st Send"; slot k>0 is "Reminder k+1".
  const knownReminders = Math.max(log.reminders_sent || 0, attempts?.length || 0, 1);
  const sendNodes = Array.from({ length: knownReminders }, (_, i) => {
    const isReminder = i > 0;
    const a = attempts?.[i];
    if (a) {
      const meta = ATTEMPT_NODE_STYLE[a.status] || { icon: Send, label: a.status || 'Attempt', state: 'sent' };
      // A reminder that actually failed/was skipped keeps that real color —
      // only a successful reminder send gets the distinct 'reminder' violet
      // instead of reusing 1st Send's blue, so the two are visually different.
      return {
        icon: isReminder && meta.state === 'sent' ? Repeat : meta.icon,
        label: i === 0 ? '1st Sent' : `Reminder ${i + 1}`,
        timestamp: a.attempted_at,
        state: isReminder && meta.state === 'sent' ? 'reminder' : meta.state,
      };
    }
    if (i === 0) {
      return { icon: Send, label: '1st Sent', timestamp: log.sent_at || log.last_attempt_at, state: log.sent ? 'sent' : (isFailed ? 'failed' : 'pending') };
    }
    return { icon: Repeat, label: `Reminder ${i + 1}`, timestamp: null, state: 'reminder' };
  });

  const outcomeNode = log.converted
    ? { icon: CheckCircle2, label: 'Converted', timestamp: log.converted_at, state: 'done' }
    : log.recurring_status === 'completed_cap'
      ? { icon: Flag, label: 'Cap reached', timestamp: null, state: 'capped' }
      : {
          icon: isFailed ? XCircle : (log.sent ? CheckCircle2 : Clock),
          label: isFailed ? 'Failed' : (log.sent ? 'Delivered' : 'Pending'),
          timestamp: log.last_attempt_at,
          // 'delivered' is its own color, distinct from the neutral blue
          // used for a mid-campaign send-in-progress AND from the emerald
          // reserved for an actual conversion — delivery isn't a win by
          // itself, just confirmation the message landed.
          state: log.sent ? 'delivered' : (isFailed ? 'failed' : 'pending'),
        };

  // The campaign's still-open next step. Usually a genuinely future event —
  // but next_reminder_at can legitimately fall into the past if the Beat
  // scan/send task is delayed or hasn't run yet, in which case it's overdue
  // rather than upcoming (a Clock icon + "in Xh" would contradide the "Xh
  // ago" relativeTime() correctly computes for an already-past timestamp).
  const nextReminderIsOverdue = log.next_reminder_at && new Date(log.next_reminder_at).getTime() < Date.now();
  // Never label/show a slot beyond the rule's actual cap — if reminders_sent
  // has already reached max_reminders, recurring_status should have flipped
  // to completed_cap server-side; showing "Reminder max+1" here would be
  // announcing a send that can't legally happen.
  const nextReminderWithinCap = !log.max_reminders || knownReminders < log.max_reminders;
  const upcomingNode = (log.next_reminder_at && !log.converted && log.recurring_status === 'scheduled' && nextReminderWithinCap)
    ? (nextReminderIsOverdue
        ? { icon: AlertTriangle, label: `Reminder ${knownReminders + 1} (due)`, timestamp: log.next_reminder_at, state: 'overdue' }
        : { icon: Clock, label: `Reminder ${knownReminders + 1}`, timestamp: log.next_reminder_at, state: 'upcoming' })
    : null;

  // Skip the plain "Delivered" outcome node when it would either restate a
  // REPEATED send (e.g. 6 real "sent" attempts, no conversion/cap yet — the
  // last attempt already IS "delivered") or when there's a genuine upcoming
  // reminder to show instead (the campaign isn't actually done, so a
  // trailing "Delivered" reads as final when it isn't). A single one-off
  // send (reminders_sent <= 1, not recurring) always keeps its own
  // "1st Send -> Delivered" pair since that IS its only outcome.
  const lastSendNode = sendNodes[sendNodes.length - 1];
  const outcomeIsRedundant = outcomeNode.state === 'delivered' && (
    (sendNodes.length > 1 && (lastSendNode?.state === 'sent' || lastSendNode?.state === 'reminder'))
    || Boolean(upcomingNode)
  );
  const timelineNodes = [
    ...sendNodes,
    ...(outcomeIsRedundant ? [] : [outcomeNode]),
    ...(upcomingNode ? [upcomingNode] : []),
  ];
  const timelineLoading = attempts === null && !attemptsError;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-40 flex items-center justify-center z-50 p-4"
      onClick={onClose}>
      <div className="bg-white rounded-xl w-full max-w-2xl shadow-xl max-h-[85vh] overflow-y-auto"
        onClick={(e) => e.stopPropagation()}>
        {/* Header — avatar, name, status at a glance */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100 sticky top-0 bg-white rounded-t-xl">
          <div className="flex items-center gap-3 min-w-0">
            <div className="w-10 h-10 rounded-full bg-gradient-to-br from-blue-50 to-indigo-50 text-blue-600 flex items-center justify-center font-semibold text-sm shrink-0">
              {(userName || '?').charAt(0).toUpperCase()}
            </div>
            <div className="min-w-0">
              <h3 className="text-base font-semibold text-gray-900 truncate">{userName || 'Unknown subscriber'}</h3>
              <div className="flex items-center gap-3 flex-wrap">
                {log.user?.mobile_no && <InfoChip icon={Phone}>{log.user.mobile_no}</InfoChip>}
                {log.user?.ref_code && <InfoChip icon={Hash}>{log.user.ref_code}</InfoChip>}
              </div>
            </div>
          </div>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600 shrink-0"><X className="w-5 h-5" /></button>
        </div>

        <div className="px-6 py-5 space-y-6">
          {/* Journey — trigger -> target, as a flow, matching the Rules page's chip style */}
          <div>
            <h4 className="text-xs font-semibold text-gray-400 uppercase tracking-wider mb-2">Journey</h4>
            <div className="bg-gray-50 border border-gray-200 rounded-xl p-4">
              <p className="text-sm font-semibold text-gray-900 mb-2">{log.journey_name || 'Unnamed step'}</p>
              <div className="flex items-center gap-2 flex-wrap">
                <span className="inline-flex items-center gap-1.5 bg-blue-50 border border-blue-200 rounded-full pl-2.5 pr-3 py-1">
                  <Zap className="w-3 h-3 text-blue-500 shrink-0" />
                  <code className="text-blue-700 font-mono text-[11px]">{log.rule_match_config?.event_name || '—'}</code>
                </span>
                <ArrowRight className="w-3.5 h-3.5 text-gray-300 shrink-0" />
                {log.converted ? (
                  <span className="inline-flex items-center gap-1.5 bg-emerald-50 border border-emerald-200 rounded-full pl-2.5 pr-3 py-1">
                    <CheckCircle2 className="w-3 h-3 text-emerald-500 shrink-0" />
                    <code className="text-emerald-700 font-mono text-[11px]">{log.target_event_name || 'Converted'}</code>
                  </span>
                ) : log.target_event_name ? (
                  <span className="inline-flex items-center gap-1.5 bg-white border border-dashed border-gray-300 rounded-full pl-2.5 pr-3 py-1">
                    <span className="w-1.5 h-1.5 rounded-full bg-gray-300 shrink-0" />
                    <code className="text-gray-500 font-mono text-[11px]">{log.target_event_name}</code>
                    <span className="text-gray-400 text-[11px] italic">not yet</span>
                  </span>
                ) : (
                  <span className="inline-flex items-center gap-1.5 bg-white border border-dashed border-gray-300 rounded-full pl-2.5 pr-3 py-1">
                    <span className="w-1.5 h-1.5 rounded-full bg-gray-300 shrink-0" />
                    <span className="text-gray-400 text-[11px] italic">not yet</span>
                  </span>
                )}
              </div>
            </div>

            {log.application_id && (
              <div className="flex items-center justify-between gap-3 bg-indigo-50 border border-indigo-200 rounded-xl px-4 py-3 mt-3">
                <span className="text-xs font-semibold text-indigo-700 uppercase tracking-wider">Application ID</span>
                <div className="text-indigo-900">
                  <CopyableId value={log.application_id} displayLength={36} />
                </div>
              </div>
            )}
          </div>

          {/* Send timeline — temporarily disabled. Flip `false &&` back to
              re-enable. */}
          {false && <div>
            <h4 className="text-xs font-semibold text-gray-400 uppercase tracking-wider mb-3">
              Send Timeline{timelineLoading && <span className="ml-2 text-gray-300 normal-case font-normal">loading exact times…</span>}
            </h4>
            <div className="bg-gray-50 border border-gray-200 rounded-xl p-4 overflow-x-auto">
              <div className={`flex items-start min-w-fit ${timelineLoading ? 'opacity-50' : ''}`}>
                {timelineNodes.map((node, i) => (
                  <TimelineNode key={`${node.label}-${i}`} {...node} isLast={i === timelineNodes.length - 1} />
                ))}
              </div>
              {log.error_message && (
                <div className="mt-4 pt-3 border-t border-gray-200 text-xs text-red-700 bg-red-50 rounded-lg px-3 py-2">
                  <span className="font-medium">Error:</span> {log.error_message}
                </div>
              )}
            </div>
          </div>}

          {/* Recurring campaign — dot-stepper progress instead of a raw count.
              Temporarily disabled. Flip `false &&` back to re-enable. */}
          {false && <div>
            <h4 className="text-xs font-semibold text-gray-400 uppercase tracking-wider mb-2 inline-flex items-center">Recurring Campaign<CampaignStatusHelp /></h4>
            <div className="bg-gray-50 border border-gray-200 rounded-xl p-4">
              {isRecurring ? (
                <>
                  <div className="flex items-center justify-between mb-3">
                    <div className="flex items-center gap-2">
                      <Repeat className="w-4 h-4 text-blue-500" />
                      <span className="text-sm font-medium text-gray-800">{log.reminders_sent ?? 0} reminder{log.reminders_sent === 1 ? '' : 's'} sent</span>
                    </div>
                    <RecurringStatusBadge status={log.recurring_status} />
                  </div>
                  <div className="flex items-center gap-1.5 pt-4">
                    {Array.from({ length: maxKnown }).map((_, i) => {
                      const isFilled = i < (log.reminders_sent || 0);
                      const attempt = attempts?.[i];
                      const dotLabel = i === 0 ? '1st send' : `Reminder ${i + 1}`;
                      const dotDetail = !isFilled
                        ? 'Not sent yet'
                        : attempt
                          ? fmt(attempt.attempted_at)
                          : 'Exact time not recorded';
                      // Custom hover tooltip instead of the native `title`
                      // attribute — title's rendering is inconsistent
                      // (delayed, sometimes suppressed by ancestor styles)
                      // and gives no control over appearance.
                      return (
                        <div key={i} className="relative flex-1 group">
                          <div className={`h-2 rounded-full cursor-default ${isFilled ? 'bg-blue-500' : 'bg-gray-200'}`} />
                          <div className="pointer-events-none absolute bottom-full left-1/2 -translate-x-1/2 mb-1.5 opacity-0 group-hover:opacity-100 transition-opacity whitespace-nowrap bg-gray-900 text-white text-[11px] rounded-md px-2 py-1 shadow-lg z-10">
                            <span className="font-medium">{dotLabel}</span> — {dotDetail}
                            <div className="absolute top-full left-1/2 -translate-x-1/2 border-4 border-transparent border-t-gray-900" />
                          </div>
                        </div>
                      );
                    })}
                  </div>
                  {log.next_reminder_at && !log.converted && log.recurring_status === 'scheduled' && (
                    nextReminderIsOverdue ? (
                      <p className="text-xs text-amber-700 mt-3 flex items-center gap-1.5">
                        <AlertTriangle className="w-3.5 h-3.5 text-amber-500" /> Reminder overdue — was due {relativeTime(log.next_reminder_at)} ({fmt(log.next_reminder_at)})
                      </p>
                    ) : (
                      <p className="text-xs text-gray-500 mt-3 flex items-center gap-1.5">
                        <Clock className="w-3.5 h-3.5 text-gray-400" /> Next reminder {relativeTime(log.next_reminder_at)} ({fmt(log.next_reminder_at)})
                      </p>
                    )
                  )}
                </>
              ) : (
                <p className="text-sm text-gray-400 flex items-center gap-2"><Ban className="w-4 h-4" /> Not a recurring rule — single send only.</p>
              )}
            </div>
          </div>}
        </div>
      </div>
    </div>
  );
};

const EngagementMessageLogs = () => {
  const navigate = useNavigate();
  const [logs, setLogs] = useState([]);
  const [count, setCount] = useState(0);
  // KPI card counts — aggregated server-side across the FULL filtered
  // result set (see events/api/views_engagement_sent/list.py's
  // item_frequencies), not just the current page's 25 rows.
  const [statusCounts, setStatusCounts] = useState({});
  const [page, setPage] = useState(1);
  const [status, setStatus] = useState('');
  // Campaign filter dropdown is temporarily disabled (see the commented-out
  // <select> below) — no setter exists right now since nothing can change
  // this value; restore as useState('') alongside the dropdown when it
  // comes back.
  const recurringStatusFilter = '';
  const [eventNameFilter, setEventNameFilter] = useState('');
  const [search, setSearch] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [selectedLog, setSelectedLog] = useState(null);

  const loadLogs = useCallback(async () => {
    setLoading(true);
    setError('');

    const res = await fetchEngagementSent({
      status: status || undefined,
      recurring_status: recurringStatusFilter || undefined,
      event_name: eventNameFilter || undefined,
      search: search || undefined,
      page,
      items: PAGE_SIZE,
    });

    if (res?.metadata?.api_status) {
      setLogs(res.msg?.items || []);
      setCount(res.msg?.pagination?.total || 0);
      setStatusCounts(res.msg?.status_counts || {});
    } else {
      setLogs([]);
      setCount(0);
      setStatusCounts({});
      setError(typeof res?.msg === 'string' ? res.msg : 'Failed to load send history');
    }
    setLoading(false);
  }, [status, recurringStatusFilter, eventNameFilter, search, page]);

  useEffect(() => { loadLogs(); }, [loadLogs]);

  const onFilter = (setter) => (e) => { setter(e.target.value); setPage(1); };

  const totalPages = Math.max(1, Math.ceil(count / PAGE_SIZE));

  return (
    <div className="min-h-screen bg-gray-50 p-4 sm:p-8 flex justify-center">
      <div className="w-full max-w-7xl space-y-6">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
          <div className="flex items-center gap-3">
            <button
              onClick={() => navigate('/subscriber-wallet')}
              className="p-2 text-gray-500 hover:text-gray-900 hover:bg-white rounded-lg border border-transparent hover:border-gray-200 transition-all"
              aria-label="Back to home">
              <ArrowLeft className="w-4 h-4" />
            </button>
            <button
              onClick={() => navigate('/subscriber-wallet/engagement')}
              className="p-2 text-gray-500 hover:text-gray-900 hover:bg-white rounded-lg border border-transparent hover:border-gray-200 transition-all"
              aria-label="Back to Customer Engagement">
              <ChevronRight className="w-4 h-4 rotate-180" />
            </button>
            <div>
              <h1 className="text-xl font-bold text-gray-900 leading-tight">Engagement Logs</h1>
              <p className="text-sm text-gray-400">WhatsApp send &amp; delivery history</p>
            </div>
          </div>
          <button
            onClick={loadLogs}
            className="self-start sm:self-auto px-4 py-2.5 bg-white border border-gray-200 text-gray-700 rounded-xl text-sm font-medium hover:bg-gray-50 shadow-sm flex items-center gap-2 transition-all">
            <RefreshCw className="w-4 h-4" /> Refresh
          </button>
        </div>

        {/* Module tabs */}
        <EngagementTabs active="logs" />

        {/* KPI cards */}
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3">
          {KPI_CARDS.map((c, i) => (
            <StatCard key={c.key} index={i} icon={c.icon} label={c.label} accent={c.accent}
              loading={loading} value={statusCounts[c.key] ?? 0} compact />
          ))}
        </div>

        {/* Filter bar — search matches Ref Code or Mobile Number (the backend
            also accepts an Application ID here, same /search contract as
            Application Events, but that's not surfaced in this UI's
            placeholder since the column isn't shown in this table), plus
            Event Name and Status which are specific to this ledger. */}
        <div className="sticky top-4 z-20 bg-white rounded-2xl shadow-sm border border-gray-200 p-4">
          <div className="flex flex-wrap items-center gap-x-6 gap-y-3">
            <div className="relative flex-1 min-w-[16rem] max-w-md">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
              <input
                value={search}
                onChange={onFilter(setSearch)}
                placeholder="Search by Ref Code or Mobile Number"
                className="w-full pl-9 pr-3 py-2 border border-gray-200 rounded-lg text-sm bg-gray-50 focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
            </div>
            <div className="flex items-center gap-2">
              <span className="text-xs font-medium text-gray-400">Event name</span>
              <input
                value={eventNameFilter}
                onChange={onFilter(setEventNameFilter)}
                placeholder="e.g. vc_slot_page_viewed"
                className="px-3 py-1.5 border border-gray-200 rounded-lg text-sm bg-gray-50 focus:ring-2 focus:ring-blue-500 focus:border-transparent w-56"
              />
            </div>
            <div className="flex items-center gap-2">
              <ListChecks className="w-4 h-4 text-gray-400" />
              <span className="text-xs font-medium text-gray-400">Status</span>
              <select value={status} onChange={onFilter(setStatus)}
                className="px-3 py-1.5 border border-gray-200 rounded-lg text-sm bg-gray-50 focus:ring-2 focus:ring-blue-500 focus:border-transparent">
                <option value="">All statuses</option>
                {STATUS_OPTIONS.map((s) => (
                  <option key={s.value} value={s.value}>{s.label}</option>
                ))}
              </select>
            </div>
            {/* Campaign filter temporarily disabled — remove this comment
                wrapper to restore.
            <div className="flex items-center gap-2">
              <Repeat className="w-4 h-4 text-gray-400" />
              <span className="text-xs font-medium text-gray-400 inline-flex items-center">Campaign<CampaignStatusHelp /></span>
              <select value={recurringStatusFilter} onChange={onFilter(setRecurringStatusFilter)}
                className="px-3 py-1.5 border border-gray-200 rounded-lg text-sm bg-gray-50 focus:ring-2 focus:ring-blue-500 focus:border-transparent">
                <option value="">All campaigns</option>
                {RECURRING_STATUS_OPTIONS.map((s) => (
                  <option key={s.value} value={s.value}>{s.label}</option>
                ))}
              </select>
            </div>
            */}
          </div>
        </div>

        {error && (
          <div className="px-4 py-3 text-sm text-red-700 bg-red-50 border border-red-100 rounded-xl">{error}</div>
        )}

        {/* Table */}
        <div className="bg-white rounded-2xl shadow-sm border border-gray-200 overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-50 sticky top-0">
                <tr>
                  {['Last Attempt', 'Journey', 'User', 'Status'].map((h, i) => (
                    <th key={i} className={`px-6 py-3 text-xs font-medium text-gray-500 uppercase tracking-wider ${i === 0 ? 'text-left' : 'text-center'}`}>{h}</th>
                  ))}
                  {/* Campaign column temporarily disabled — remove this
                      comment wrapper to restore.
                  <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
                    <span className="inline-flex items-center justify-center">Campaign<CampaignStatusHelp /></span>
                  </th>
                  */}
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-100">
                {logs.length === 0 ? (
                  <tr>
                    <td colSpan="4" className="px-6 py-16 text-center">
                      {loading ? (
                        <div className="flex flex-col items-center gap-2 text-gray-400">
                          <RefreshCw className="w-6 h-6 animate-spin" /><span className="text-sm">Loading logs…</span>
                        </div>
                      ) : (
                        <div className="flex flex-col items-center gap-3 text-gray-400">
                          <div className="w-14 h-14 rounded-2xl bg-gray-50 flex items-center justify-center">
                            <ListChecks className="w-6 h-6 text-gray-300" />
                          </div>
                          <p className="text-sm font-medium text-gray-500">No sends for the selected filters</p>
                        </div>
                      )}
                    </td>
                  </tr>
                ) : (
                  logs.map((log) => {
                    const userName = log.user?.full_name || [log.user?.first_name, log.user?.last_name].filter(Boolean).join(' ');
                    return (
                      <tr key={log.id} onClick={() => setSelectedLog(log)}
                        className="hover:bg-blue-50/40 cursor-pointer transition-colors">
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 text-left">{fmt(log.last_attempt_at)}</td>
                        <td className="px-6 py-4 whitespace-nowrap text-center">
                          <p className="text-sm font-medium text-gray-900">{log.journey_name || '—'}</p>
                          {log.rule_match_config?.event_name && (
                            <p className="text-xs text-gray-400">{log.rule_match_config.event_name}</p>
                          )}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-center">
                          <p className="text-gray-800 font-medium">{userName || '—'}</p>
                          {log.user?.mobile_no && <p className="text-xs text-gray-400">{log.user.mobile_no}</p>}
                          {log.user?.ref_code && <p className="text-xs text-gray-400">{log.user.ref_code}</p>}
                        </td>
                        <td className="px-6 py-4 text-center"><StatusBadge status={log.last_attempt_status} /></td>
                        {/* Campaign column temporarily disabled — remove this
                            comment wrapper to restore.
                        <td className="px-6 py-4 text-center"><RecurringStatusBadge status={log.recurring_status} /></td>
                        */}
                      </tr>
                    );
                  })
                )}
              </tbody>
            </table>
          </div>

          {!loading && count > 0 && (
            <div className="px-6 py-4 border-t border-gray-100 flex items-center justify-between">
              <div className="text-sm text-gray-500">
                Showing {(page - 1) * PAGE_SIZE + 1}–{Math.min(page * PAGE_SIZE, count)} of {count}
              </div>
              <div className="flex items-center gap-2">
                <button disabled={page === 1} onClick={() => setPage((p) => Math.max(1, p - 1))}
                  className={`px-3 py-1.5 border rounded-lg text-sm transition-colors ${page === 1 ? 'text-gray-300 border-gray-200 cursor-not-allowed' : 'text-gray-700 hover:bg-gray-50 border-gray-300'}`}>Prev</button>
                <button disabled={page >= totalPages} onClick={() => setPage((p) => (p < totalPages ? p + 1 : p))}
                  className={`px-3 py-1.5 border rounded-lg text-sm transition-colors ${page >= totalPages ? 'text-gray-300 border-gray-200 cursor-not-allowed' : 'text-gray-700 hover:bg-gray-50 border-gray-300'}`}>Next</button>
              </div>
            </div>
          )}
        </div>
      </div>

      <LogDetailModal log={selectedLog} onClose={() => setSelectedLog(null)} />
    </div>
  );
};

export default EngagementMessageLogs;
